import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Check, DollarSign, Link as LinkIcon, TrendingUp, Shield } from 'lucide-react';
import SafeHTML from '../components/SafeHTML';

const PartnershipPage = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    const message = `Partnership Inquiry from ${formData.name}. Email: ${formData.email}. Message: ${formData.message}`;
    window.open(`https://wa.me/971585862103?text=${encodeURIComponent(message)}`, '_blank');
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      <section 
        className="relative h-[500px] bg-cover bg-center"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=1920&q=80)',
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-transparent"></div>
        <div className="relative h-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center">
          <div className="max-w-2xl">
            <div className="inline-block bg-yellow-400 text-black px-4 py-2 rounded font-bold mb-4">
              {t('affiliateBadge')}
            </div>
            <h1 className="text-5xl lg:text-6xl font-bold text-white mb-6">
              {t('affiliateHeroTitle')}
            </h1>
            <p className="text-xl text-white/90 mb-8">
              {t('affiliateHeroSubtitle')}
            </p>
            <a
              href="#partnership-form"
              className="inline-block bg-gradient-to-r from-logo-blue to-logo-cyan text-white px-8 py-4 rounded-lg font-semibold text-lg hover:shadow-xl transition-all"
            >
              {t('affiliateHeroBtn')}
            </a>
          </div>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        
        <div className="prose prose-lg max-w-none mb-16">
          <SafeHTML tag="p" className="text-gray-700 text-lg leading-relaxed" html={t('affiliateIntro1')} />
          <SafeHTML tag="p" className="text-gray-700 text-lg leading-relaxed mt-4" html={t('affiliateIntro2')} />
        </div>

        <div className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">{t('affiliateHowToTitle')}</h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-logo-blue rounded-full flex items-center justify-center text-white text-2xl font-bold mx-auto mb-4">1</div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">{t('affiliateStep1Title')}</h3>
              <p className="text-gray-600">
                {t('affiliateStep1Desc')}
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-logo-blue rounded-full flex items-center justify-center text-white text-2xl font-bold mx-auto mb-4">2</div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">{t('affiliateStep2Title')}</h3>
              <p className="text-gray-600">
                {t('affiliateStep2Desc')}
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-logo-blue rounded-full flex items-center justify-center text-white text-2xl font-bold mx-auto mb-4">3</div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">{t('affiliateStep3Title')}</h3>
              <p className="text-gray-600">
                {t('affiliateStep3Desc')}
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-logo-blue rounded-full flex items-center justify-center text-white text-2xl font-bold mx-auto mb-4">4</div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">{t('affiliateStep4Title')}</h3>
              <p className="text-gray-600">
                {t('affiliateStep4Desc')}
              </p>
            </div>
          </div>
        </div>

        <div className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">{t('affiliateAdvantagesTitle')}</h2>
          
          <div className="grid md:grid-cols-2 gap-8">
            <div className="p-6 bg-blue-50 rounded-lg">
              <DollarSign className="w-12 h-12 text-logo-blue mb-4" />
              <h3 className="text-xl font-bold text-gray-900 mb-3">{t('affiliateAdv1Title')}</h3>
              <p className="text-gray-700">
                {t('affiliateAdv1Desc')}
              </p>
            </div>

            <div className="p-6 bg-blue-50 rounded-lg">
              <LinkIcon className="w-12 h-12 text-logo-blue mb-4" />
              <h3 className="text-xl font-bold text-gray-900 mb-3">{t('affiliateAdv2Title')}</h3>
              <p className="text-gray-700">
                {t('affiliateAdv2Desc')}
              </p>
            </div>

            <div className="p-6 bg-blue-50 rounded-lg">
              <TrendingUp className="w-12 h-12 text-logo-blue mb-4" />
              <h3 className="text-xl font-bold text-gray-900 mb-3">{t('affiliateAdv3Title')}</h3>
              <p className="text-gray-700">
                {t('affiliateAdv3Desc')}
              </p>
            </div>

            <div className="p-6 bg-blue-50 rounded-lg">
              <Shield className="w-12 h-12 text-logo-blue mb-4" />
              <h3 className="text-xl font-bold text-gray-900 mb-3">{t('affiliateAdv4Title')}</h3>
              <SafeHTML tag="p" className="text-gray-700" html={t('affiliateAdv4Desc')} />
            </div>
          </div>
        </div>

        <div className="mb-16 bg-gradient-to-r from-logo-blue to-logo-cyan p-8 rounded-lg text-white text-center">
          <h2 className="text-3xl font-bold mb-4">{t('affiliateCTATitle')}</h2>
          <p className="text-xl mb-6">{t('affiliateCTASubtitle')}</p>
          <a
            href="#partnership-form"
            className="inline-block bg-white text-logo-blue px-8 py-4 rounded-lg font-semibold text-lg hover:shadow-xl transition-all"
          >
            {t('affiliateCTABtn')}
          </a>
        </div>

        <div id="partnership-form" className="max-w-2xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">{t('affiliateFormTitle')}</h2>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
                {t('contactName')} *
              </label>
              <input
                type="text"
                id="name"
                required
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-logo-blue focus:border-transparent"
                placeholder={t('contactNamePlaceholder')}
              />
            </div>
            
            <div>
              <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                {t('affiliateFormEmail')} *
              </label>
              <input
                type="email"
                id="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-logo-blue focus:border-transparent"
                placeholder={t('affiliateFormEmailPlaceholder')}
              />
            </div>
            
            <div>
              <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2">
                {t('contactPhone')} *
              </label>
              <input
                type="tel"
                id="phone"
                required
                value={formData.phone}
                onChange={(e) => setFormData({...formData, phone: e.target.value})}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-logo-blue focus:border-transparent"
                placeholder={t('contactPhonePlaceholder')}
              />
            </div>
            
            <div>
              <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">
                {t('affiliateFormMessage')}
              </label>
              <textarea
                id="message"
                rows="4"
                value={formData.message}
                onChange={(e) => setFormData({...formData, message: e.target.value})}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-logo-blue focus:border-transparent"
                placeholder={t('affiliateFormMessagePlaceholder')}
              ></textarea>
            </div>
            
            <button
              type="submit"
              className="w-full bg-gradient-to-r from-logo-blue to-logo-cyan text-white px-8 py-4 rounded-lg font-semibold text-lg hover:shadow-xl transition-all"
            >
              {t('affiliateFormSendBtn')}
            </button>
          </form>
        </div>

      </div>

      <Footer />
    </div>
  );
};

export default PartnershipPage;
